import React, { useState, useEffect, useContext, useMemo } from 'react'
import MainLayout from '../../layout/MainLayout'
import PollForm from '../../components/PollForm'
import Modal from '../../components/Modal'
import PaginationBar from '../../components/PaginationBar'
import { AuthContext } from '../../context/AuthContext'
import { api } from '../../api/axios'
import { toast } from 'react-toastify'
import { tableDateFormate } from '../../utils/dateHelper'
import { fileUlr } from '../../api/url'
import {
    Plus, Search, Eye, EyeOff, Pencil, Trash2, X, BarChart3,
    CheckCircle2, Users, ThumbsUp, ImageIcon,
} from 'lucide-react'

const PAGE_SIZE = 9

export default function Polls() {
    const { loading } = useContext(AuthContext)
    const [pollsData, setPollsData] = useState([])
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [createOpen, setCreateOpen] = useState(false)

    // Detail drawer — submissions for a selected poll
    const [selected, setSelected] = useState(null)
    const [submissions, setSubmissions] = useState(null)
    const [subsLoading, setSubsLoading] = useState(false)

    // Edit modal
    const [editOpen, setEditOpen] = useState(false)
    const [editData, setEditData] = useState(null)

    const handelGetData = async () => {
        try {
            const res = await api.get('/polls')
            setPollsData(res.data || [])
        } catch (err) {
            console.error('Error fetching polls:', err)
            toast.error('Failed to load polls')
        }
    }

    useEffect(() => { handelGetData() }, [loading])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return pollsData.filter(p => {
            if (q && !p.question?.toLowerCase().includes(q)) return false
            if (typeFilter && p.pollType !== typeFilter) return false
            if (statusFilter === 'active' && p.isActive !== true) return false
            if (statusFilter === 'inactive' && p.isActive !== false) return false
            return true
        })
    }, [pollsData, search, typeFilter, statusFilter])

    const pageStart = (page - 1) * PAGE_SIZE
    const paged = filtered.slice(pageStart, pageStart + PAGE_SIZE)

    useEffect(() => { setPage(1) }, [search, typeFilter, statusFilter])

    const stats = useMemo(() => ({
        total: pollsData.length,
        active: pollsData.filter(p => p.isActive).length,
        textType: pollsData.filter(p => p.pollType === 'text').length,
        imageType: pollsData.filter(p => p.pollType === 'image').length,
    }), [pollsData])

    const openDetails = async (poll) => {
        setSelected(poll)
        setSubmissions(null)
        setSubsLoading(true)
        try {
            const res = await api.get(`/polls/submit/${poll._id}`)
            setSubmissions(res.data)
        } catch (err) {
            console.error('Error fetching submissions:', err)
            setSubmissions({ pollSubmits: [] })
        } finally {
            setSubsLoading(false)
        }
    }

    const handelDeletePoll = async (id, e) => {
        e?.stopPropagation?.()
        if (!window.confirm('Delete this poll? This cannot be undone.')) return
        try {
            const res = await api.delete(`/polls/${id}`)
            if (res.status === 200) {
                toast.success('Poll deleted')
                handelGetData()
                if (selected?._id === id) setSelected(null)
            }
        } catch {
            toast.error('Delete failed')
        }
    }

    const handleTogglePollStatus = async (poll, e) => {
        e?.stopPropagation?.()
        try {
            await api.patch(`/polls/active/${poll._id}`, { isActive: !poll.isActive })
            toast.success(poll.isActive ? 'Poll hidden' : 'Poll activated')
            handelGetData()
        } catch {
            toast.error('Update failed')
        }
    }

    const handleOpenEditModal = (item, e) => {
        e?.stopPropagation?.()
        setEditData({
            id: item._id,
            pollType: item.pollType,
            question: item.question,
            option1: item.option1,
            option2: item.option2,
            option3: item.option3,
            option4: item.option4,
            correctOption: item.correctOption || '',
        })
        setEditOpen(true)
    }

    const handleSubmitPollEditForm = async (e) => {
        e.preventDefault()
        const formData = new FormData(e.target)
        const data = Object.fromEntries(formData)
        try {
            const res = await api.patch(`/polls/${editData.id}`, data)
            if (res.status === 200) {
                toast.success('Poll updated')
                setEditOpen(false)
                handelGetData()
            }
        } catch {
            toast.error('Update failed')
        }
    }

    const HeaderActions = (
        <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
        >
            <Plus size={16} /> New Poll
        </button>
    )

    return (
        <MainLayout title="Polls" subtitle={`${stats.total} polls • ${stats.active} active`} Buttons={HeaderActions}>
            {/* Stat strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatTile label="Total" value={stats.total} Icon={BarChart3} />
                <StatTile label="Active" value={stats.active} Icon={CheckCircle2} />
                <StatTile label="Text" value={stats.textType} Icon={BarChart3} />
                <StatTile label="Image" value={stats.imageType} Icon={ImageIcon} />
            </div>

            {/* Toolbar */}
            <div className="bg-white border border-gray-200 rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by question…"
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-gray-900"
                    />
                </div>
                <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="px-3 py-2 text-sm rounded-lg border border-gray-200"
                >
                    <option value="">All types</option>
                    <option value="text">Text</option>
                    <option value="image">Image</option>
                </select>
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="px-3 py-2 text-sm rounded-lg border border-gray-200"
                >
                    <option value="">All status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
                {(search || typeFilter || statusFilter) && (
                    <button
                        onClick={() => { setSearch(''); setTypeFilter(''); setStatusFilter('') }}
                        className="text-sm text-gray-500 hover:text-gray-900"
                    >
                        Clear
                    </button>
                )}
                <div className="ml-auto text-sm text-gray-500">
                    {filtered.length} result{filtered.length === 1 ? '' : 's'}
                </div>
            </div>

            {/* Grid */}
            {paged.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center text-gray-400">
                    <BarChart3 size={32} className="mx-auto text-gray-300 mb-2" />
                    No polls match your filters.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {paged.map(poll => (
                        <PollCard
                            key={poll._id}
                            poll={poll}
                            onOpen={() => openDetails(poll)}
                            onEdit={e => handleOpenEditModal(poll, e)}
                            onToggle={e => handleTogglePollStatus(poll, e)}
                            onDelete={e => handelDeletePoll(poll._id, e)}
                        />
                    ))}
                </div>
            )}

            <div className="mt-5">
                <PaginationBar
                    totalItems={filtered.length}
                    page={page}
                    pageSize={PAGE_SIZE}
                    onChange={setPage}
                />
            </div>

            {/* Detail drawer */}
            {selected && (
                <PollDetailDrawer
                    poll={selected}
                    submissions={submissions}
                    loading={subsLoading}
                    onClose={() => setSelected(null)}
                />
            )}

            {/* Create modal */}
            {createOpen && (
                <Modal title="Create new poll" closeModal={() => setCreateOpen(false)}>
                    <PollForm />
                </Modal>
            )}

            {/* Edit modal */}
            {editOpen && editData && (
                <Modal title="Edit poll" closeModal={() => setEditOpen(false)}>
                    <form onSubmit={handleSubmitPollEditForm} className="w-[36rem] max-w-full space-y-4">
                        <Field label="Question" required>
                            <input name="question" type="text" defaultValue={editData.question} required className={inputCls} />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            {['option1', 'option2', 'option3', 'option4'].map((k, i) => (
                                <Field key={k} label={`Option ${String.fromCharCode(65 + i)}`} required={editData.pollType === 'text'}>
                                    <input
                                        name={k}
                                        type="text"
                                        defaultValue={editData[k]}
                                        disabled={editData.pollType === 'image'}
                                        required={editData.pollType === 'text'}
                                        className={inputCls}
                                    />
                                </Field>
                            ))}
                        </div>
                        <Field label="Correct answer (optional — shown after users submit)">
                            <select name="correctOption" defaultValue={editData.correctOption || ''} className={inputCls}>
                                <option value="">— None —</option>
                                {['option1', 'option2', 'option3', 'option4'].map((k, i) => (
                                    <option key={k} value={editData[k]}>
                                        {String.fromCharCode(65 + i)}: {editData[k] || `(image)`}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setEditOpen(false)}
                                className="px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100">Cancel</button>
                            <button type="submit"
                                className="px-4 py-2 rounded-lg text-sm bg-gray-900 text-white hover:bg-gray-800">Save changes</button>
                        </div>
                    </form>
                </Modal>
            )}
        </MainLayout>
    )
}

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-gray-900'

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="block text-xs font-medium text-gray-700 mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </span>
        {children}
    </label>
)

const StatTile = ({ label, value, Icon }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-gray-100 text-gray-700 flex items-center justify-center">
            <Icon size={16} />
        </div>
        <div>
            <div className="text-xs text-gray-500 font-medium">{label}</div>
            <div className="text-xl font-bold text-gray-900 tabular-nums">{value}</div>
        </div>
    </div>
)

function PollCard({ poll, onOpen, onEdit, onToggle, onDelete }) {
    const options = ['option1', 'option2', 'option3', 'option4']
    const pct = poll.percentages || {}
    const votes = poll.voteCount ?? 0
    const isImage = poll.pollType === 'image'

    return (
        <div
            onClick={onOpen}
            className="bg-white border border-gray-200 rounded-2xl p-4 cursor-pointer hover:border-gray-400 transition-colors flex flex-col"
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${poll.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                    {poll.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="text-[11px] text-gray-400 uppercase">{poll.pollType}</span>
            </div>

            <h3 className="text-base font-semibold text-gray-900 mb-3 line-clamp-2 min-h-[3rem]">
                {poll.question}
            </h3>

            <div className="space-y-2 mb-4">
                {options.map((k, i) => {
                    const v = poll[k]
                    const p = pct[k] ?? 0
                    return (
                        <div key={k} className="relative">
                            {isImage ? (
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="font-semibold text-gray-500 w-4">{String.fromCharCode(65 + i)}.</span>
                                    {v ? (
                                        <img src={`${fileUlr}/uploads/${v}`} alt="" className="h-10 w-16 object-cover rounded border border-gray-200" />
                                    ) : <span className="text-gray-400">—</span>}
                                    <span className="ml-auto text-gray-700 font-semibold">{p}%</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 relative bg-gray-100 rounded overflow-hidden h-7">
                                        <div className="absolute inset-y-0 left-0 bg-gray-200 rounded" style={{ width: `${p}%` }} />
                                        <div className="absolute inset-0 flex items-center px-2 text-xs">
                                            <span className="font-semibold text-gray-500 mr-1.5">{String.fromCharCode(65 + i)}.</span>
                                            <span className="truncate text-gray-800">{v || '—'}</span>
                                        </div>
                                    </div>
                                    <span className="text-xs font-semibold text-gray-700 w-10 text-right tabular-nums">{p}%</span>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            <div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1"><Users size={12} />{votes}</span>
                    <span className="inline-flex items-center gap-1"><ThumbsUp size={12} />{poll.likes ?? 0}</span>
                    <span>{tableDateFormate(poll.updatedAt)}</span>
                </div>
                <div className="flex items-center gap-1">
                    <IconBtn onClick={onToggle} title={poll.isActive ? 'Hide' : 'Activate'} Icon={poll.isActive ? Eye : EyeOff} />
                    <IconBtn onClick={onEdit} title="Edit" Icon={Pencil} color="text-blue-600 hover:bg-blue-50" />
                    <IconBtn onClick={onDelete} title="Delete" Icon={Trash2} color="text-red-600 hover:bg-red-50" />
                </div>
            </div>
        </div>
    )
}

const IconBtn = ({ onClick, title, Icon, color = 'text-gray-600 hover:bg-gray-100' }) => (
    <button
        onClick={onClick}
        title={title}
        className={`p-1.5 rounded ${color} transition-colors`}
    >
        <Icon size={16} />
    </button>
)

function PollDetailDrawer({ poll, submissions, loading, onClose }) {
    const list = submissions?.pollSubmits || []
    return (
        <div className="fixed inset-0 z-40 flex justify-end">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col">
                <div className="px-5 py-4 border-b flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider mb-1">Poll details</div>
                        <div className="font-semibold text-gray-900">{poll.question}</div>
                    </div>
                    <button onClick={onClose} className="p-2 -mr-2 rounded-full hover:bg-gray-100">
                        <X size={18} />
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-5 space-y-5">
                    <div className="flex items-center gap-6 text-sm">
                        <div>
                            <div className="text-xs text-gray-500">Total votes</div>
                            <div className="text-xl font-bold">{poll.voteCount ?? list.length}</div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Likes</div>
                            <div className="text-xl font-bold">{poll.likes ?? 0}</div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Status</div>
                            <div className="text-sm font-semibold">{poll.isActive ? 'Active' : 'Inactive'}</div>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Submissions ({list.length})</div>
                        {loading ? (
                            <div className="text-sm text-gray-400 py-6 text-center">Loading submissions…</div>
                        ) : list.length === 0 ? (
                            <div className="text-sm text-gray-400 py-6 text-center">No one has voted yet.</div>
                        ) : (
                            <ul className="divide-y divide-gray-100">
                                {list.map(sub => (
                                    <li key={sub._id} className="py-3 flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-700">
                                            {(sub.userId?.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{sub.userId?.name || '—'}</div>
                                            <div className="text-xs text-gray-500 truncate">@{sub.userId?.username || '—'}</div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-xs text-gray-400">{tableDateFormate(sub.createdAt)}</div>
                                            <div className="text-xs text-gray-700 font-semibold max-w-[14ch] truncate">
                                                {submissions?.pollType === 'image' ? (
                                                    <img src={`${fileUlr}/uploads/${sub.option}`} alt="" className="h-5 w-12 object-cover rounded border border-gray-200 ml-auto" />
                                                ) : (
                                                    sub.option
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
